<template>
  <el-container class="address-book-page">
    <!-- 左侧树 -->
    <resizable-aside class="aside" :default-width="300" :min-width="200" :max-width="500" storage-key="address-book-aside-width">
      <div class="header">
        <el-input
            v-model="keyword"
            size="small"
            placeholder="搜索姓名/部门"
            prefix-icon="el-icon-search"
            clearable
        />
      </div>

      <div class="tree-wrap">
        <virtual-scroller
            :key="virtualScrollerKey"
            ref="virtualScroller"
            :items="flattenedTreeData"
            :size="40"
            class="virtual-scroller-container"
        >
          <template v-slot="{ item }">
            <div
                class="tree-node-wrapper"
                :style="{ paddingLeft: item.level * 12 + 'px' }"
                :class="{ active: activeNodeId === item.id }"
                @click="onNodeClick(item)"
                @contextmenu.prevent="item.type === 'USER' && showRightMenu($event, item)"
            >
              <!-- 部门 -->
              <div v-if="item.type === 'DEPT'" class="tree-node dept-node">
                <i
                    class="iconfont"
                    :class="expandedKeys.includes(item.id) ? 'icon-icon_folder_open1' : 'icon-icon_folder1'"
                    @click.stop="toggleExpand(item)"
                />
                <span class="dept-name">{{ item.name }}</span>
                <span class="dept-count">({{ item.userCount }})</span>
              </div>

              <!-- 人员 -->
              <div v-else class="tree-node user-node">
                <head-image :name="item.nickname" :size="24" :url="item.avatar" />
                <span class="nickname">{{ item.nickname }}</span>
              </div>
            </div>
          </template>
        </virtual-scroller>
      </div>
    </resizable-aside>

    <!-- 右侧详情 -->
    <el-main class="main">
      <div v-if="!userInfo.id" class="empty-tip">
        <no-data-tip text="请选择联系人" />
      </div>

      <div v-else class="detail">
        <div class="header">{{ userInfo.nickName }}</div>
        <div class="info">
          <head-image
              :size="160"
              :name="userInfo.nickName"
              :url="userInfo.headImageThumb"
              :radius="'10%'"
              @click.native="showFullImage"
          />
          <el-descriptions :column="1" border class="user-descriptions">
            <el-descriptions-item label="部门">{{ userInfo.deptName }}</el-descriptions-item>
            <el-descriptions-item label="昵称">{{ userInfo.nickName }}</el-descriptions-item>
            <el-descriptions-item label="性别">{{ userInfo.sex == 1 ? '男' : '女' }}</el-descriptions-item>
            <el-descriptions-item label="签名">{{ userInfo.signature }}</el-descriptions-item>
          </el-descriptions>
        </div>

        <div class="btn-group">
          <el-button type="primary" icon="el-icon-chat-line-round" @click="onSendMessage(userInfo)">
            发消息
          </el-button>
        </div>
      </div>
    </el-main>

    <right-menu ref="rightMenu" @select="onSelectMenu" />
    <chat-selector ref="chatSel" title="分享名片" />
    <friend-apply ref="applyRef" />
  </el-container>
</template>

<script>
import ResizableAside from '../components/common/ResizableAside.vue';

export default {
  name: 'AddressBook',
  components: {
    HeadImage: () => import('@/components/common/HeadImage.vue'),
    NoDataTip: () => import('@/components/common/NoDataTip.vue'),
    RightMenu: () => import('@/components/common/RightMenu.vue'),
    ChatSelector: () => import('@/components/chat/ChatSelector.vue'),
    FriendApply: () => import('@/components/friend/FriendApply.vue'),
    VirtualScroller: () => import('@/components/common/VirtualScroller.vue'),
    ResizableAside
  },
  data() {
    return {
      keyword: '',
      virtualScrollerKey: 0,
      allDepts: [],           // 缓存完整后端数据
      treeData: [],           // 当前真正用于渲染的树
      expandedKeys: [],
      activeNodeId: null,
      flattenedTreeData: [],
      userInfo: {},
      menuItems: [
        { key: 'CHAT', name: '发送消息' },
        { key: 'CARD', name: '分享名片' },
        // { key: 'ADD', name: '添加好友' }
      ]
    };
  },
  mounted() {
    this.loadAll();
  },
  methods: {
    /* ----------------- 数据获取 ----------------- */
    loadAll() {
      // 只加载一次完整数据
      this.$http.get('/addressBook/user').then(res => {
        this.allDepts = res || [];
        this.treeData = this.buildTree(this.allDepts);
        this.flattenedTreeData = this.flattenTree(this.treeData);
      });
    },

    /* ----------------- 树构造 ----------------- */
    buildTree(depts) {
      return depts.map(d => {
        const children = d.children ? this.buildTree(d.children) : [];
        const users = (d.users || []).map(u => ({
          id: u.id,
          nickname: u.nickname,
          avatar: u.headImageThumb,
          deptName: d.name,
          type: 'USER'
        }));
        const userCount = users.length;
        return { ...d, userCount, children: [...children, ...users] };
      });
    },

    flattenTree(treeData, level = 0) {
      let res = [];
      treeData.forEach(node => {
        res.push({ ...node, level });
        if (this.expandedKeys.includes(node.id) && node.children?.length) {
          res = res.concat(this.flattenTree(node.children, level + 1));
        }
      });
      return res;
    },

    toggleExpand(node) {
      const idx = this.expandedKeys.indexOf(node.id);
      idx > -1 ? this.expandedKeys.splice(idx, 1) : this.expandedKeys.push(node.id);
      this.flattenedTreeData = this.flattenTree(this.treeData);
      this.virtualScrollerKey += 1;
    },

    /* ----------------- 前端搜索核心 ----------------- */
    filterDepts(depts, kw) {
      if (!kw) return depts;
      const key = kw.toLowerCase();
      return depts
          .map(d => {
            // 过滤用户
            const users = (d.users || []).filter(
                u =>
                    u.nickname?.toLowerCase().includes(key) ||
                    u.userName?.toLowerCase().includes(key)
            );
            // 递归过滤子部门
            const children = d.children ? this.filterDepts(d.children, key) : [];
            // 只要部门名、用户、子部门任意一个匹配即保留
            const deptMatch = d.name.toLowerCase().includes(key);
            const keep = deptMatch || users.length || children.length;
            return keep ? { ...d, users, children } : null;
          })
          .filter(Boolean);
    },

    /* ----------------- 事件处理 ----------------- */
    onNodeClick(data) {
      this.activeNodeId = data.id;
      if (data.type === 'USER') {
        this.$http.get(`/user/find/${data.id}`).then(user => {
          this.userInfo = { ...user, deptName: data.deptName };
        });
      } else {
        this.toggleExpand(data);
      }
    },

    /* ----------------- 其余交互（与原实现完全一致） ----------------- */
    onSendMessage(friend) {
      this.$http
          .post('/friend/add', null, { params: { friendId: friend.id } })
          .then(() => {
            const chat = {
              type: 'PRIVATE',
              targetId: friend.id,
              showName: friend.nickname,
              headImage: friend.headImage,
              isDnd: friend.isDnd,
              isTop: friend.isTop
            };
            this.chatStore.openChat(chat);
            this.chatStore.setActiveChat(chat);
            this.$router.push('/home/chat');
          });
    },
    onAddFriend() {
      this.$refs.applyRef?.open(this.userInfo);
    },
    showRightMenu(e, user) {
      this.userInfo = user;
      this.userInfo.nickName = user.nickname;
      this.$refs.rightMenu.open(e, this.menuItems);
    },
    onSelectMenu(item) {
      switch (item.key) {
        case 'CHAT':
          this.onSendMessage(this.userInfo);
          break;
        case 'CARD':
          this.onSendCard(this.userInfo);
          break;
        case 'ADD':
          this.onAddFriend();
          break;
      }
    },
    onSendCard(friend) {
      this.$refs.chatSel.open(chats => {
        let idx = 0;
        chats.forEach(chat => {
          const cardData = {
            userId: friend.id,
            nickName: friend.nickname,
            headImage: friend.avatar
          };
          const msgInfo = {
            type: this.$enums.MESSAGE_TYPE.USER_CARD,
            content: JSON.stringify(cardData),
            recvId: chat.type === 'PRIVATE' ? chat.targetId : undefined,
            groupId: chat.type === 'GROUP' ? chat.targetId : undefined
          };
          const action = `/message/${chat.type.toLowerCase()}/send`;
          this.$http({ url: action, method: 'post', data: msgInfo }).then(m => {
            m.selfSend = true;
            this.chatStore.openChat(chat);
            this.chatStore.insertMessage(m, chat);
            if (++idx === chats.length) this.$message.success('发送成功');
          });
        });
      });
    },
    showFullImage() {
      if (this.userInfo.headImage) {
        this.$eventBus.$emit('openFullImage', this.userInfo.headImage);
      }
    }
  },

  /* ----------------- 监听关键词 ----------------- */
  watch: {
    keyword(val) {
      const kw = (val || '').trim();
      const filtered = this.filterDepts(this.allDepts, kw);
      this.treeData = this.buildTree(filtered);
      this.flattenedTreeData = this.flattenTree(this.treeData);
      this.virtualScrollerKey += 1;
    }
  }
};
</script>


<style scoped>
.address-book-page {
  height: 100vh; /* 修复：使用视口高度作为基准 */
  display: flex;
}

.address-book-page .header {
  padding: 10px;
  border-bottom: 1px solid #e8e8e8;
}

.aside {
  background: #fcfdff;
  border-right: 1px solid #e8e8e8;
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
}

/* 修复：使用flex:1自适应剩余高度 */
.aside .tree-wrap {
  flex: 1;
  overflow: hidden;
}

/* 修复：使用100%高度继承父容器 */
.virtual-scroller-container {
  height: 100%;
  overflow-y: auto;
}

.main {
  padding: 0;
  height: 100%;
  overflow: hidden;
}

.main .empty-tip {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
}

.main .header {
  padding: 12px;
  font-size: 18px;
  border-bottom: 1px solid #e8e8e8;
}

.main .info {
  display: flex;
  padding: 20px;
  justify-content: center;
}

.main .info .user-descriptions {
  margin-left: 20px;
  width: 300px;
}

.main .btn-group {
  padding: 0 20px 20px;
  display: flex;
  justify-content: center;
}

.tree-node-wrapper {
  height: 40px; /* 固定高度，与虚拟滚动size保持一致 */
  line-height: 40px;
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.tree-node-wrapper:hover {
  background: #e6f4ff;
}

.tree-node-wrapper.active {
  background: #d8edff;
}

.tree-node {
  display: flex;
  align-items: center;
  font-size: 14px;
  width: 100%;
  height: 100%;
}

.tree-node .iconfont {
  margin-right: 6px;
  color: #606266;
  cursor: pointer;
  flex-shrink: 0;
}

.tree-node.dept-node {
  font-weight: 600;
  color: #303133;
}

.tree-node.dept-node .dept-name {
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.tree-node.dept-node .dept-count {
  color: #999;
  font-size: 12px;
  margin-right: 10px;
  flex-shrink: 0;
}

.tree-node.user-node {
  color: #606266;
  padding-right: 10px;
  width: 100%;
}

.tree-node.user-node .nickname {
  margin-left: 6px;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
